import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '../hooks/useAuth';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/Button';
import { Card, CardHeader, CardBody } from '../components/Card';
import { Alert } from '../components/Alert';
import { memberAPI } from '../api/memberAPI';
import { regionAPI } from '../api/regionAPI';
import { mapAPI } from '../api/mapAPI';
import { parseMonthDay, daysUntil } from '../utils/dateEvents';
import { FilterPopover } from '../components/FilterPopover';
import { FilterBar } from '../components/FilterBar';

const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const DAYS_SHORT = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];
const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'];

export function Calendar() {
    const { isAuthenticated, isAdmin } = useAuth();
    const navigate = useNavigate();

    const [currentMonth, setCurrentMonth] = useState(() => {
        const now = new Date();
        return new Date(now.getFullYear(), now.getMonth(), 1);
    });
    const [members, setMembers] = useState([]);
    const [leaders, setLeaders] = useState([]);
    const [regions, setRegions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [eventFilter, setEventFilter] = useState('all');
    const [regionFilter, setRegionFilter] = useState('');
    const [selectedDay, setSelectedDay] = useState(() => new Date().getDate());
    const [isMobile, setIsMobile] = useState(() => window.innerWidth < 640);

    useEffect(() => {
        const onResize = () => setIsMobile(window.innerWidth < 640);
        window.addEventListener('resize', onResize);
        return () => window.removeEventListener('resize', onResize);
    }, []);

    // Reset selected day when month changes (pick today if viewing current month, else 1st)
    useEffect(() => {
        const now = new Date();
        if (currentMonth.getFullYear() === now.getFullYear() && currentMonth.getMonth() === now.getMonth()) {
            setSelectedDay(now.getDate());
        } else {
            setSelectedDay(1);
        }
    }, [currentMonth]);

    useEffect(() => {
        if (!isAuthenticated || !isAdmin) return;
        regionAPI.getRegions().then((res) => { setRegions((res.data.data || {}).items || []); }).catch(() => {});
    }, [isAuthenticated, isAdmin]);

    useEffect(() => {
        if (!isAuthenticated) return;
        mapAPI.getMapData().then((res) => { setLeaders(res.data.leaders || []); }).catch(() => {});
    }, [isAuthenticated]);

    const fetchMembers = useCallback(async () => {
        setLoading(true);
        try {
            const params = { per_page: 1000 };
            if (regionFilter) params.region_id = regionFilter;
            const res = await memberAPI.getMembers(params);
            setMembers((res.data.data || {}).items || []);
            setError(null);
        } catch (err) { setError(err.response?.data?.message || 'Failed to load members'); }
        finally { setLoading(false); }
    }, [regionFilter]);

    useEffect(() => { if (isAuthenticated) fetchMembers(); }, [isAuthenticated, fetchMembers]);

    const events = useMemo(() => {
        const list = [];
        const addPerson = (p) => {
            if (eventFilter !== 'anniversaries') {
                const bd = parseMonthDay(p.birthdate);
                if (bd) list.push({ type: 'birthday', name: p.name, month: bd.month, day: bd.day, date: p.birthdate });
            }
            if (eventFilter !== 'birthdays') {
                const wa = parseMonthDay(p.wedding_anniversary);
                if (wa) list.push({ type: 'anniversary', name: p.name, month: wa.month, day: wa.day, date: p.wedding_anniversary });
            }
        };
        members.forEach(addPerson);
        leaders.forEach(addPerson);
        return list;
    }, [members, leaders, eventFilter]);

    const calendarDays = useMemo(() => {
        const year = currentMonth.getFullYear();
        const month = currentMonth.getMonth();
        const firstDay = new Date(year, month, 1).getDay();
        const daysInMonth = new Date(year, month + 1, 0).getDate();
        const prevMonthDays = new Date(year, month, 0).getDate();
        const cells = [];
        for (let i = firstDay - 1; i >= 0; i--) cells.push({ day: prevMonthDays - i, outside: true });
        const today = new Date();
        for (let d = 1; d <= daysInMonth; d++) {
            const isToday = today.getFullYear() === year && today.getMonth() === month && today.getDate() === d;
            const dayEvents = events.filter((e) => e.month === month + 1 && e.day === d);
            cells.push({ day: d, outside: false, today: isToday, events: dayEvents });
        }
        const remaining = 7 - (cells.length % 7);
        if (remaining < 7) for (let i = 1; i <= remaining; i++) cells.push({ day: i, outside: true });
        return cells;
    }, [currentMonth, events]);

    const selectedDayEvents = useMemo(() => {
        const month = currentMonth.getMonth() + 1;
        return events.filter((e) => e.month === month && e.day === selectedDay);
    }, [events, currentMonth, selectedDay]);

    const upcomingEvents = useMemo(() => {
        return events.map((e) => ({ ...e, daysAway: daysUntil(e.month, e.day) }))
            .filter((e) => e.daysAway <= 31)
            .sort((a, b) => a.daysAway - b.daysAway);
    }, [events]);

    const prevMonth = () => setCurrentMonth((d) => new Date(d.getFullYear(), d.getMonth() - 1, 1));
    const nextMonth = () => setCurrentMonth((d) => new Date(d.getFullYear(), d.getMonth() + 1, 1));
    const goToday = () => { const now = new Date(); setCurrentMonth(new Date(now.getFullYear(), now.getMonth(), 1)); };

    if (!isAuthenticated) {
        return (
            <div className="flex items-center justify-center min-h-[60vh] p-4">
                <Card><CardBody><h2 className="text-lg font-semibold text-slate-800 dark:text-slate-100 mb-2">Not authenticated</h2><p className="text-sm text-slate-500 mb-4">Please login to view the calendar.</p><Button onClick={() => navigate('/login')}>Login</Button></CardBody></Card>
            </div>
        );
    }

    const monthLabel = `${MONTHS[currentMonth.getMonth()]} ${currentMonth.getFullYear()}`;

    return (
        <div className="p-4 lg:p-6 max-w-content mx-auto animate-fade-in">
            <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 mb-4">
                <div>
                    <h1 className="text-xl font-bold text-slate-800 dark:text-slate-100">Calendar</h1>
                    <p className="text-sm text-slate-500 mt-0.5">Birthdays and wedding anniversaries</p>
                </div>
                <div className="flex flex-wrap gap-2">
                    <Button variant="secondary" onClick={() => navigate('/dashboard')}>Back</Button>
                </div>
            </div>

            {error && <Alert type="error" onClose={() => setError(null)}>{error}</Alert>}

            <FilterBar
                search={
                    <div className="flex items-center gap-2">
                        <button onClick={prevMonth} className="w-8 h-8 rounded-lg flex items-center justify-center text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors" aria-label="Previous month">&larr;</button>
                        <h2 className="text-base font-semibold text-slate-800 dark:text-slate-100 min-w-[160px] text-center">{monthLabel}</h2>
                        <button onClick={nextMonth} className="w-8 h-8 rounded-lg flex items-center justify-center text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors" aria-label="Next month">&rarr;</button>
                        <Button size="sm" variant="secondary" onClick={goToday}>Today</Button>
                    </div>
                }
                filters={<>
                    <FilterPopover
                        label="Events"
                        value={eventFilter === 'all' ? '' : eventFilter}
                        onChange={(v) => setEventFilter(v || 'all')}
                        options={[
                            { value: 'birthdays', label: 'Birthdays Only' },
                            { value: 'anniversaries', label: 'Anniversaries Only' },
                        ]}
                        allLabel="All Events"
                    />
                    {isAdmin && (
                        <FilterPopover
                            label="Region"
                            value={regionFilter}
                            onChange={setRegionFilter}
                            options={regions.map((r) => ({ value: r.id, label: r.name }))}
                            allLabel="All Regions"
                        />
                    )}
                </>}
                activeFilterCount={(eventFilter !== 'all' ? 1 : 0) + (regionFilter ? 1 : 0)}
                onClearFilters={() => { setEventFilter('all'); setRegionFilter(''); }}
            />

            {loading ? (
                <p className="text-center text-slate-500 py-6">Loading...</p>
            ) : (
                <>
                <Card>
                    <CardBody className="!p-0">
                        <div className="grid grid-cols-7">
                            {(isMobile ? DAYS_SHORT : DAYS).map((d, i) => (
                                <div key={i} className="px-1 py-2 text-center text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400 border-b border-slate-200 dark:border-slate-700">{d}</div>
                            ))}
                            {calendarDays.map((cell, i) => {
                                const cellEvents = cell.events || [];
                                const isSelected = !cell.outside && cell.day === selectedDay;
                                if (isMobile) {
                                    // Mobile: compact cells with dot indicators
                                    const maxDots = 3;
                                    return (
                                        <div
                                            key={i}
                                            className={`min-h-[48px] p-1 border-b border-r border-slate-100 dark:border-slate-700/50 flex flex-col items-center cursor-pointer transition-colors ${cell.outside ? 'bg-slate-50/50 dark:bg-slate-800/30' : ''} ${isSelected ? 'bg-accent-50 dark:bg-accent-900/20' : ''}`}
                                            onClick={() => { if (!cell.outside) setSelectedDay(cell.day); }}
                                        >
                                            <span className={`text-sm font-medium w-7 h-7 flex items-center justify-center rounded-full ${
                                                cell.outside ? 'text-slate-300 dark:text-slate-600'
                                                : cell.today ? 'bg-accent-500 text-white font-bold'
                                                : isSelected ? 'ring-2 ring-accent-500 text-accent-600 dark:text-accent-400'
                                                : 'text-slate-700 dark:text-slate-300'
                                            }`}>
                                                {cell.day}
                                            </span>
                                            {cellEvents.length > 0 && (
                                                <div className="flex gap-0.5 mt-1">
                                                    {cellEvents.slice(0, maxDots).map((ev, j) => (
                                                        <span key={j} className={`w-1.5 h-1.5 rounded-full ${ev.type === 'birthday' ? 'bg-emerald-500' : 'bg-blue-500'}`} />
                                                    ))}
                                                    {cellEvents.length > maxDots && (
                                                        <span className="text-[9px] leading-none text-slate-400 ml-0.5">+{cellEvents.length - maxDots}</span>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    );
                                }
                                // Desktop: full event text in cells
                                const maxShow = 3;
                                return (
                                    <div key={i} className={`min-h-[100px] p-1.5 border-b border-r border-slate-100 dark:border-slate-700/50 ${cell.outside ? 'bg-slate-50/50 dark:bg-slate-800/30' : ''} ${cell.today ? 'bg-accent-50/50 dark:bg-accent-900/10' : ''}`}>
                                        <div className={`text-xs font-medium mb-1 ${cell.outside ? 'text-slate-300 dark:text-slate-600' : cell.today ? 'text-accent-600 dark:text-accent-400 font-bold' : 'text-slate-700 dark:text-slate-300'}`}>
                                            {cell.day}
                                        </div>
                                        {cellEvents.slice(0, maxShow).map((ev, j) => (
                                            <div key={j} className={`text-xs leading-snug px-1.5 py-1 rounded mb-1 truncate border-l-2 font-medium ${
                                                ev.type === 'birthday'
                                                    ? 'bg-emerald-50 text-emerald-700 border-emerald-500 dark:bg-emerald-900/20 dark:text-emerald-400'
                                                    : 'bg-blue-50 text-blue-700 border-blue-500 dark:bg-blue-900/20 dark:text-blue-400'
                                            }`} title={`${ev.name} — ${ev.type === 'birthday' ? 'Birthday' : 'Anniversary'}`}>
                                                {ev.type === 'birthday' ? '🎂 ' : '💍 '}{ev.name}
                                            </div>
                                        ))}
                                        {cellEvents.length > maxShow && (
                                            <span className="text-xs text-slate-400">+{cellEvents.length - maxShow} more</span>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                        {/* Mobile: selected day event panel */}
                        {isMobile && (
                            <div className="border-t border-slate-200 dark:border-slate-700 px-4 py-3">
                                <h3 className="text-sm font-semibold text-slate-800 dark:text-slate-100 mb-2">
                                    {MONTHS[currentMonth.getMonth()]} {selectedDay}
                                </h3>
                                {selectedDayEvents.length === 0 ? (
                                    <p className="text-sm text-slate-400 py-2">No events</p>
                                ) : (
                                    <div className="space-y-1.5">
                                        {selectedDayEvents.map((ev, j) => (
                                            <div key={j} className="flex items-center gap-3 py-1.5">
                                                <span className="text-base flex-shrink-0">{ev.type === 'birthday' ? '🎂' : '💍'}</span>
                                                <span className="flex-1 text-sm text-slate-700 dark:text-slate-300">{ev.name}</span>
                                                <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${
                                                    ev.type === 'birthday'
                                                        ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400'
                                                        : 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400'
                                                }`}>
                                                    {ev.type === 'birthday' ? 'Birthday' : 'Anniversary'}
                                                </span>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        )}
                        <div className="flex items-center gap-4 px-4 py-2 border-t border-slate-200 dark:border-slate-700 text-xs text-slate-500">
                            <span className="flex items-center gap-1.5"><span className="w-3 h-1.5 rounded-full bg-emerald-500"></span> Birthday</span>
                            <span className="flex items-center gap-1.5"><span className="w-3 h-1.5 rounded-full bg-blue-500"></span> Anniversary</span>
                        </div>
                    </CardBody>
                </Card>

                <Card className="mt-4">
                    <CardHeader><h2 className="text-base font-semibold text-slate-800 dark:text-slate-100">Upcoming Events ({upcomingEvents.length})</h2></CardHeader>
                    <CardBody>
                        {upcomingEvents.length === 0 ? (
                            <p className="text-center text-slate-500 py-6">No upcoming events in the next 31 days</p>
                        ) : (
                            <div className="space-y-1">
                                {upcomingEvents.map((ev, i) => (
                                    <div key={i} className="flex items-center gap-3 py-2">
                                        <span className="text-xs font-medium text-slate-500 w-12 flex-shrink-0">{MONTHS[ev.month - 1].slice(0, 3)} {ev.day}</span>
                                        <span className="flex-1 text-sm text-slate-700 dark:text-slate-300">{ev.name}</span>
                                        <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${
                                            ev.type === 'birthday'
                                                ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400'
                                                : 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400'
                                        }`}>
                                            {ev.type === 'birthday' ? 'Birthday' : 'Anniversary'}
                                        </span>
                                        <span className="text-xs text-slate-400 w-16 text-right flex-shrink-0">
                                            {ev.daysAway === 0 ? 'Today' : ev.daysAway === 1 ? 'Tomorrow' : `${ev.daysAway}d`}
                                        </span>
                                    </div>
                                ))}
                            </div>
                        )}
                    </CardBody>
                </Card>
                </>
            )}
        </div>
    );
}

export default Calendar;
